import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type JSX, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, History, Pencil, Send, XCircle, Clock } from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { useConfirm } from '../components/ui/Confirm';
import { Button } from '../components/ui/Button';
import { TextAreaField } from '../components/ui/Field';
import { Skeleton } from '../components/ui/Skeleton';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { RelationPanel } from '../components/shared/RelationPanel';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { GatedSection } from '../components/shared/GatedSection';
import { formatDate, formatINR } from '../lib/format';
import {
  WithdrawalMethodChip,
  WithdrawalReviewDrawer,
  WithdrawalStatusChip,
  WITHDRAWAL_STATUS_LABEL,
  type WithdrawalRequest,
} from '../components/wallet';

export const Route = createFileRoute('/_authed/finance/withdrawals/$withdrawalId')({
  component: WithdrawalDetailPage,
});

// ---------------------------------------------------------------------------
// Wire shapes — the backend agent is producing GET /api/wallet/withdrawals/:id
// enriched with joined users, owner profile, balance, ledger debit, and
// recent withdrawals for the same owner (§4.20 of the detail-pages plan).
// Every embed is typed as nullable so this page renders against partial
// responses while the backend is in flight — and we degrade gracefully if a
// row predates the requested_by backfill.
// ---------------------------------------------------------------------------

interface UserEmbed {
  id: string;
  displayName: string | null;
  email: string | null;
}

interface OwnerEmbed {
  type: 'teacher' | 'student';
  id: string;
  name: string;
  /** Wallet-context summary for the right rail. */
  balancePaise?: number | null;
}

interface LedgerDebitEmbed {
  id: string;
  amountPaise: number;
  entryType: 'referral_credit' | 'withdrawal_debit' | 'admin_adjustment' | 'clawback_debit';
  notes: string | null;
  createdAt: string;
  academicYear: string;
}

interface RecentWithdrawalEmbed {
  id: string;
  requestedAt: string;
  status: WithdrawalRequest['status'];
  requestedAmountPaise: number;
}

interface FeeCreditTargetEmbed {
  studentId: string;
  studentName: string;
}

interface WithdrawalDetail {
  withdrawal: WithdrawalRequest;
  /** Server may omit during backfill — we degrade to the base row. */
  owner?: OwnerEmbed | null;
  /** Owner-balance KPI; backend may also include it on `owner.balancePaise`. */
  ownerBalancePaise?: number | null;
  requestedBy?: UserEmbed | null;
  reviewedBy?: UserEmbed | null;
  paidBy?: UserEmbed | null;
  ledgerDebit?: LedgerDebitEmbed | null;
  recentWithdrawals?: ReadonlyArray<RecentWithdrawalEmbed>;
  /** Resolved target-student name for `method = fee_credit`. */
  feeCreditTarget?: FeeCreditTargetEmbed | null;
}

const FINANCE_ROLES = new Set<string>(['super_admin', 'finance_admin']);

const DAY_MS = 24 * 60 * 60 * 1000;

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

type DestinationShape = {
  upiId?: string;
  ifsc?: string;
  accountNumber?: string;
  accountName?: string;
  targetStudentId?: string;
};

const parseDestination = (raw: unknown): DestinationShape | null => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return isPlainObject(parsed) ? (parsed as DestinationShape) : null;
    } catch {
      return null;
    }
  }
  if (isPlainObject(raw)) return raw as DestinationShape;
  return null;
};

/**
 * Most-recent status timestamp — the "Days in current status" KPI counts from
 * here. Falls back through the lifecycle in the order the row could have
 * reached its current status.
 */
const mostRecentStatusAt = (req: WithdrawalRequest): string => {
  if (req.status === 'paid' && req.paidAt) return req.paidAt;
  if ((req.status === 'rejected' || req.status === 'approved' || req.status === 'cancelled') && req.reviewedAt) {
    return req.reviewedAt;
  }
  return req.requestedAt;
};

const daysSince = (iso: string): number => {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / DAY_MS));
};

const relativeFromNow = (iso: string): string => {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const diff = t - Date.now();
  const days = Math.round(diff / DAY_MS);
  if (days === 0) return 'today';
  if (days > 0) return `in ${days} day${days === 1 ? '' : 's'}`;
  return `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`;
};

const userLabel = (u: UserEmbed | null | undefined, fallback = 'unknown'): string =>
  u?.displayName?.trim() || u?.email?.trim() || fallback;

function WithdrawalDetailPage(): JSX.Element {
  const { withdrawalId } = Route.useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const canFinance = role !== null && FINANCE_ROLES.has(role);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const detailQuery = useQuery({
    // Keep the key prefix matching the drawer's invalidation
    // (`['wallet', 'withdrawals', ...]`) so approve/reject/mark-paid through
    // either the inline drawer or the detail Edit button keeps this page
    // fresh.
    queryKey: ['wallet', 'withdrawals', 'detail', withdrawalId] as const,
    queryFn: ({ signal }) =>
      apiRequest<WithdrawalDetail>(
        `/api/wallet/withdrawals/${encodeURIComponent(withdrawalId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['wallet', 'withdrawals'] });
  };

  const cancelMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/wallet/withdrawals/${encodeURIComponent(withdrawalId)}/cancel`,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => {
      toast('Withdrawal request cancelled.', { variant: 'success' });
      invalidate();
    },
    onError: (err: unknown) =>
      toast(err instanceof ApiError ? err.message : 'Could not cancel.', {
        variant: 'error',
      }),
  });

  const approveMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/wallet/withdrawals/${encodeURIComponent(withdrawalId)}/approve`,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => {
      toast('Approved. Mark paid once the transfer is completed.', {
        variant: 'success',
      });
      invalidate();
    },
    onError: (err: unknown) =>
      toast(err instanceof ApiError ? err.message : 'Could not approve.', {
        variant: 'error',
      }),
  });

  const rejectMutation = useMutation({
    mutationFn: (reason: string) =>
      apiRequest<{ ok: true }>(
        `/api/wallet/withdrawals/${encodeURIComponent(withdrawalId)}/reject`,
        { method: 'POST', body: { reason } },
      ),
    onSuccess: () => {
      toast('Withdrawal rejected.', { variant: 'success' });
      setRejectOpen(false);
      setRejectReason('');
      invalidate();
    },
    onError: (err: unknown) =>
      toast(err instanceof ApiError ? err.message : 'Could not reject.', {
        variant: 'error',
      }),
  });

  const data = detailQuery.data;
  const withdrawal = data?.withdrawal;

  // Resolve a usable owner view even if the backend hasn't (yet) shipped the
  // enriched `owner` embed. The base row always carries ownerType/ownerRefId
  // — for both teachers and students `ownerRefId` is the profile id, so the
  // profile link still works without the name.
  const owner = useMemo<OwnerEmbed | null>(() => {
    if (!withdrawal) return null;
    if (data?.owner) return data.owner;
    return {
      type: withdrawal.ownerType,
      id: withdrawal.ownerRefId,
      name: withdrawal.ownerType === 'teacher' ? 'Teacher' : 'Student',
    };
  }, [withdrawal, data?.owner]);

  const ownerBalancePaise = useMemo(() => {
    if (!data) return null;
    if (typeof data.ownerBalancePaise === 'number') return data.ownerBalancePaise;
    if (data.owner && typeof data.owner.balancePaise === 'number') return data.owner.balancePaise;
    return null;
  }, [data]);

  // Reset transient editor state when the withdrawal id changes (e.g.
  // navigating between sibling rows in "Recent withdrawals"). Without this,
  // a typed-but-unsubmitted reject reason would carry over to a different
  // request and submit against the wrong row.
  useEffect(() => {
    setDrawerOpen(false);
    setRejectOpen(false);
    setRejectReason('');
  }, [withdrawalId]);

  const kpiItems = useMemo<KpiItem[]>(() => {
    if (!withdrawal) return [];
    const statusAt = mostRecentStatusAt(withdrawal);
    return [
      {
        label: 'Requested amount',
        value: formatINR(withdrawal.requestedAmountPaise),
      },
      {
        label: 'Owner wallet balance',
        value:
          ownerBalancePaise !== null
            ? formatINR(ownerBalancePaise)
            : '—',
        sub: owner?.name,
      },
      {
        label: 'Days in current status',
        value: daysSince(statusAt),
        sub: WITHDRAWAL_STATUS_LABEL[withdrawal.status],
      },
      {
        label: 'Cooldown ends',
        value: withdrawal.cooldownUntil
          ? formatDate(withdrawal.cooldownUntil)
          : '—',
        sub: withdrawal.cooldownUntil ? relativeFromNow(withdrawal.cooldownUntil) : undefined,
      },
    ];
  }, [withdrawal, ownerBalancePaise, owner?.name]);

  // canCancel: the backend's `/cancel` endpoint joins teachers.userId /
  // students.userId itself. We can't replicate that join in the client, so
  // approximate using requestedBy when present and fall back to "show the
  // button — let the server's 403 be authoritative". The toast on failure
  // gives the operator a clear signal if they're not the owner.
  const canCancel =
    withdrawal?.status === 'requested' &&
    (data?.requestedBy?.id ? data.requestedBy.id === currentUserId : !canFinance);

  const isFinalised =
    withdrawal?.status === 'paid' ||
    withdrawal?.status === 'rejected' ||
    withdrawal?.status === 'cancelled';

  const handleApprove = async (): Promise<void> => {
    const ok = await confirm({
      title: 'Approve this withdrawal?',
      description:
        'Approving moves the request into the payout queue. The ledger is debited only when you mark it as paid.',
      confirmLabel: 'Approve',
    });
    if (!ok) return;
    approveMutation.mutate();
  };

  const handleSubmitReject = (): void => {
    const reason = rejectReason.trim();
    if (!reason) {
      toast('Please enter a rejection reason.', { variant: 'error' });
      return;
    }
    rejectMutation.mutate(reason);
  };

  const handleCancel = async (): Promise<void> => {
    const ok = await confirm({
      title: 'Cancel this withdrawal request?',
      description:
        'The request will be withdrawn. You can submit a new request while the window is open.',
      confirmLabel: 'Cancel request',
      variant: 'danger',
    });
    if (!ok) return;
    cancelMutation.mutate();
  };

  const headerTitle = withdrawal
    ? `Withdrawal · ${formatINR(withdrawal.requestedAmountPaise)}`
    : 'Withdrawal';

  const headerSublabel =
    withdrawal && owner
      ? `${owner.name} · ${owner.type === 'teacher' ? 'Teacher' : 'Student'} · ${withdrawal.academicYear}`
      : null;

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={detailQuery.error}
        title="Withdrawal request"
        onRetry={() => detailQuery.refetch()}
      >
        {detailQuery.isLoading || !data || !withdrawal ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={headerTitle}
              sublabel={headerSublabel}
              statusChip={
                <span className="flex items-center gap-2">
                  <WithdrawalStatusChip status={withdrawal.status} />
                  <WithdrawalMethodChip method={withdrawal.method} />
                </span>
              }
              backTo="/finance/withdrawals"
              actions={
                <>
                  {canFinance && withdrawal.status === 'requested' ? (
                    <>
                      <Button
                        size="sm"
                        icon={<CheckCircle2 size={14} aria-hidden="true" />}
                        onClick={() => {
                          void handleApprove();
                        }}
                        loading={approveMutation.isPending}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        icon={<XCircle size={14} aria-hidden="true" />}
                        onClick={() => setRejectOpen(true)}
                      >
                        Reject
                      </Button>
                    </>
                  ) : null}
                  {canFinance && withdrawal.status === 'approved' ? (
                    <Button
                      size="sm"
                      icon={<Send size={14} aria-hidden="true" />}
                      onClick={() => setDrawerOpen(true)}
                    >
                      Mark paid
                    </Button>
                  ) : null}
                  {canCancel ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={<XCircle size={14} aria-hidden="true" />}
                      onClick={() => {
                        void handleCancel();
                      }}
                      loading={cancelMutation.isPending}
                    >
                      Cancel request
                    </Button>
                  ) : null}
                  {canFinance ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={<Pencil size={14} aria-hidden="true" />}
                      onClick={() => setDrawerOpen(true)}
                    >
                      {isFinalised ? 'View' : 'Edit'}
                    </Button>
                  ) : null}
                </>
              }
            />

            {isFinalised ? (
              <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-4 py-2 text-xs text-[color:var(--color-text-muted)]">
                This request is finalised and is read-only.
              </div>
            ) : null}

            {rejectOpen ? (
              <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                  Reject this request
                </h3>
                <div className="mt-3 space-y-3">
                  <TextAreaField
                    label="Rejection reason"
                    rows={3}
                    placeholder="The owner sees this verbatim."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setRejectOpen(false);
                        setRejectReason('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      icon={<XCircle size={14} aria-hidden="true" />}
                      onClick={handleSubmitReject}
                      loading={rejectMutation.isPending}
                      disabled={!rejectReason.trim()}
                    >
                      Reject withdrawal
                    </Button>
                  </div>
                </div>
              </section>
            ) : null}

            <KpiStrip items={kpiItems} />

            <DestinationCard
              method={withdrawal.method}
              destination={parseDestination(withdrawal.destination)}
              feeCreditTarget={data.feeCreditTarget ?? null}
            />

            <LifecycleCard detail={data} />

            {owner ? (
              <OwnerCard
                owner={owner}
                balancePaise={ownerBalancePaise}
                academicYear={withdrawal.academicYear}
              />
            ) : null}

            <RecentWithdrawalsCard
              currentId={withdrawal.id}
              recent={data.recentWithdrawals ?? []}
              onOpen={(id) =>
                void navigate({
                  to: '/finance/withdrawals/$withdrawalId',
                  params: { withdrawalId: id },
                })
              }
            />

            {data.ledgerDebit ? (
              <LedgerDebitCard ledger={data.ledgerDebit} />
            ) : null}

            <NotesCard
              notes={withdrawal.notes}
              rejectionReason={withdrawal.rejectionReason}
            />

            <ActivityPanel
              targetType="withdrawal_request"
              targetId={withdrawal.id}
              title="Activity"
            />
          </div>
        )}
      </GatedSection>

      <WithdrawalReviewDrawer
        request={drawerOpen && withdrawal ? withdrawal : null}
        currentUserId={currentUserId}
        currentUserRole={role ?? undefined}
        {...(owner?.name ? { ownerLabel: owner.name } : {})}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

interface CardShellProps {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}

const CardShell = ({ title, action, children }: CardShellProps): JSX.Element => (
  <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
    <div className="flex items-start justify-between gap-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {title}
      </h3>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
    <div className="mt-3 space-y-2 text-sm text-[color:var(--color-text)]">
      {children}
    </div>
  </section>
);

const DescField = ({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
}): JSX.Element => (
  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
    <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
      {label}
    </dt>
    <dd
      className={
        mono
          ? 'min-w-0 break-all text-right font-mono text-xs text-[color:var(--color-text)]'
          : 'min-w-0 text-right text-sm text-[color:var(--color-text)]'
      }
    >
      {value ?? <span className="text-[color:var(--color-text-muted)]">—</span>}
    </dd>
  </div>
);

interface DestinationCardProps {
  method: WithdrawalRequest['method'];
  destination: DestinationShape | null;
  feeCreditTarget: FeeCreditTargetEmbed | null;
}

function DestinationCard({
  method,
  destination,
  feeCreditTarget,
}: DestinationCardProps): JSX.Element {
  if (method === 'cash') {
    return (
      <CardShell title="Destination">
        <p className="text-sm text-[color:var(--color-text)]">Cash payout</p>
        <p className="text-xs text-[color:var(--color-text-muted)]">
          Super-admin issued; no banking destination on file.
        </p>
      </CardShell>
    );
  }

  if (method === 'fee_credit') {
    const targetId = feeCreditTarget?.studentId ?? destination?.targetStudentId ?? null;
    return (
      <CardShell title="Destination">
        <p className="text-sm font-medium text-[color:var(--color-text)]">
          Apply as fee credit
        </p>
        {feeCreditTarget && targetId ? (
          <p className="text-sm">
            <span className="text-[color:var(--color-text-muted)]">Target student: </span>
            <Link
              to="/people/students/$studentId"
              params={{ studentId: targetId }}
              className="font-medium text-gold-700 hover:underline"
            >
              {feeCreditTarget.studentName}
            </Link>
          </p>
        ) : targetId ? (
          <p className="text-xs">
            <span className="text-[color:var(--color-text-muted)]">Target student id: </span>
            <Link
              to="/people/students/$studentId"
              params={{ studentId: targetId }}
              className="font-mono text-xs text-gold-700 hover:underline"
            >
              {targetId}
            </Link>
          </p>
        ) : (
          <p className="text-xs text-[color:var(--color-text-muted)]">
            No target student on file.
          </p>
        )}
      </CardShell>
    );
  }

  // upi_bank
  return (
    <CardShell title="Destination">
      <dl className="grid gap-2 sm:grid-cols-2">
        <DescField label="UPI ID" value={destination?.upiId ?? null} mono />
        <DescField
          label="Account holder"
          value={destination?.accountName ?? null}
        />
        <DescField
          label="Account number"
          value={destination?.accountNumber ?? null}
          mono
        />
        <DescField label="IFSC" value={destination?.ifsc ?? null} mono />
      </dl>
    </CardShell>
  );
}

interface LifecycleCardProps {
  detail: WithdrawalDetail;
}

function LifecycleCard({ detail }: LifecycleCardProps): JSX.Element {
  const { withdrawal, requestedBy, reviewedBy, paidBy } = detail;

  const reviewedLabel =
    withdrawal.status === 'rejected'
      ? 'Rejected'
      : withdrawal.status === 'cancelled'
        ? 'Cancelled'
        : 'Approved';

  return (
    <CardShell title="Lifecycle">
      <ol className="space-y-2">
        <TimelineRow
          icon={<History size={14} aria-hidden="true" />}
          label="Requested"
          at={withdrawal.requestedAt}
          actor={userLabel(requestedBy ?? null, 'unknown')}
        />
        {withdrawal.reviewedAt ? (
          <TimelineRow
            icon={<Clock size={14} aria-hidden="true" />}
            label={reviewedLabel}
            at={withdrawal.reviewedAt}
            actor={userLabel(reviewedBy ?? null, 'finance')}
          />
        ) : null}
        {withdrawal.paidAt ? (
          <TimelineRow
            icon={<Send size={14} aria-hidden="true" />}
            label="Paid"
            at={withdrawal.paidAt}
            actor={userLabel(paidBy ?? null, 'finance')}
          />
        ) : null}
        {withdrawal.status === 'rejected' && withdrawal.rejectionReason ? (
          <li className="rounded-md border border-[color:var(--color-danger)]/30 bg-[color:var(--color-danger)]/[0.06] px-3 py-2 text-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
              Rejection reason
            </p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-[color:var(--color-text)]">
              {withdrawal.rejectionReason}
            </p>
          </li>
        ) : null}
      </ol>
    </CardShell>
  );
}

interface TimelineRowProps {
  icon: ReactNode;
  label: string;
  at: string;
  actor: string;
}

const TimelineRow = ({ icon, label, at, actor }: TimelineRowProps): JSX.Element => (
  <li className="flex flex-wrap items-center gap-3 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 text-sm">
    <span className="text-gold-600">{icon}</span>
    <span className="font-medium text-[color:var(--color-text)]">{label}</span>
    <span className="text-xs text-[color:var(--color-text-muted)]">
      by <span className="text-[color:var(--color-text)]">{actor}</span>
    </span>
    <span className="ml-auto text-xs text-[color:var(--color-text-muted)] tabular-nums">
      {formatDate(at, { withTime: true })}
    </span>
  </li>
);

interface OwnerCardProps {
  owner: OwnerEmbed;
  balancePaise: number | null;
  academicYear: string;
}

function OwnerCard({ owner, balancePaise, academicYear }: OwnerCardProps): JSX.Element {
  return (
    <CardShell title="Owner & wallet context">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-[color:var(--color-text)]">
            {owner.type === 'teacher' ? (
              <Link
                to="/people/teachers/$teacherId"
                params={{ teacherId: owner.id }}
                className="hover:underline"
              >
                {owner.name}
              </Link>
            ) : (
              <Link
                to="/people/students/$studentId"
                params={{ studentId: owner.id }}
                className="hover:underline"
              >
                {owner.name}
              </Link>
            )}
          </p>
          <p className="text-xs capitalize text-[color:var(--color-text-muted)]">
            {owner.type} · {academicYear}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
            Wallet balance
          </p>
          <p className="font-display text-lg font-bold tabular-nums text-[color:var(--color-text)]">
            {balancePaise !== null ? formatINR(balancePaise) : '—'}
          </p>
        </div>
      </div>
    </CardShell>
  );
}

interface RecentWithdrawalsCardProps {
  currentId: string;
  recent: ReadonlyArray<RecentWithdrawalEmbed>;
  onOpen: (id: string) => void;
}

function RecentWithdrawalsCard({
  currentId,
  recent,
  onOpen,
}: RecentWithdrawalsCardProps): JSX.Element {
  const others = recent.filter((r) => r.id !== currentId);
  const isEmpty = others.length === 0;
  return (
    <RelationPanel
      title="Recent withdrawals by this owner"
      count={others.length}
      isEmpty={isEmpty}
      empty={{ copy: 'No other withdrawal requests on file.' }}
    >
      <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)]">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--color-surface)]">
            <tr>
              <th
                scope="col"
                className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
              >
                Requested
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
              >
                Amount
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
              >
                Status
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
              >
                <span className="sr-only">Open</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {others.map((row) => (
              <tr
                key={row.id}
                onClick={() => onOpen(row.id)}
                className="cursor-pointer border-t border-[color:var(--color-border)] transition-colors hover:bg-[color:var(--color-surface)]"
              >
                <td className="px-3 py-2 align-middle text-xs text-[color:var(--color-text-muted)] whitespace-nowrap">
                  {formatDate(row.requestedAt)}
                </td>
                <td className="px-3 py-2 align-middle text-right tabular-nums">
                  {formatINR(row.requestedAmountPaise)}
                </td>
                <td className="px-3 py-2 align-middle">
                  <WithdrawalStatusChip status={row.status} />
                </td>
                <td className="px-3 py-2 align-middle text-right">
                  <span className="text-xs text-gold-700">Open</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </RelationPanel>
  );
}

interface LedgerDebitCardProps {
  ledger: LedgerDebitEmbed;
}

function LedgerDebitCard({ ledger }: LedgerDebitCardProps): JSX.Element {
  return (
    <CardShell title="Linked ledger debit">
      <dl className="grid gap-2 sm:grid-cols-2">
        <DescField label="Amount" value={formatINR(ledger.amountPaise)} />
        <DescField label="Created" value={formatDate(ledger.createdAt, { withTime: true })} />
        <DescField label="Academic year" value={ledger.academicYear} />
        <DescField label="Entry type" value={ledger.entryType.replace(/_/g, ' ')} />
      </dl>
      {ledger.notes ? (
        <p className="mt-3 whitespace-pre-wrap text-sm text-[color:var(--color-text)]">
          {ledger.notes}
        </p>
      ) : null}
      <div className="mt-3">
        <a
          href={`/system/wallet/adjustments?ledgerId=${encodeURIComponent(ledger.id)}`}
          className="inline-flex items-center gap-1 text-xs font-medium text-gold-700 hover:underline"
        >
          Open in ledger explorer
        </a>
      </div>
    </CardShell>
  );
}

interface NotesCardProps {
  notes: string | null;
  rejectionReason: string | null;
}

function NotesCard({ notes, rejectionReason }: NotesCardProps): JSX.Element | null {
  if (!notes && !rejectionReason) return null;
  return (
    <CardShell title="Notes & rejection reason">
      {notes ? (
        <div>
          <p className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
            Owner notes
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-[color:var(--color-text)]">
            {notes}
          </p>
        </div>
      ) : null}
      {rejectionReason ? (
        <div className={notes ? 'mt-3 border-t border-[color:var(--color-border)] pt-3' : ''}>
          <p className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
            Rejection reason
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-[color:var(--color-text)]">
            {rejectionReason}
          </p>
        </div>
      ) : null}
    </CardShell>
  );
}

